import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind, snowflakeAuthTypes, getConnectorV1Name, getConnectorV2Name } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const dataflowSnowFlake: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.Snowflake,
    settings: adfModelTypeMap.snowflake.dataflowSettings,
    useInlineProjection: true,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};

export function snowflakeCommonControls(isV2: boolean = false): ControlMetadata[] {
    return [
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: isV2 ? 'accountIdentifier' : 'accountname',
                label: stringResources.ModelName_AccountName,
                placeholder: isV2 ? 'myorg-account123' : '{accountName}.east-us-2.azure',
                required: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                description: isV2 ? stringResources.Snowflake_AccountName_Desc_v2 : stringResources.Snowflake_AccountName_Desc,
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'database',
                label: stringResources.Database,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
                description: stringResources.Snowflake_Database_Desc,
                required: true,
                isLabelVertical: true
            }
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'warehouse',
                label: stringResources.DataWarehouse_DisplayText,
                isLabelVertical: true,
                required: true,
                description: stringResources.Snowflake_Warehouse_Desc,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            }
        }
    ];
}

export const dynamicPasswordControl: ControlMetadata = {
    enableAKVParameterization: true,
    hideAKVSwitcherInConnStrProps: true,
    type: 'dynamic-akv-switcher',
    bind: 'password',
    label: stringResources.ModelName_Password,
    child: {
        type: 'parameterizable',
        attribute: 'password',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_Password,
            isLabelVertical: true,
            description: stringResources.Snowflake_Password_Desc,
            required: true
        },
    },
    validators: [
        { type: 'required', errorMessage: '' }
    ],
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Snowflake_DisplayText),
        stringResources.ModelName_Password)
}

export const nameControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'user',
        label: stringResources.ModelName_UserName,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        required: true,
        isLabelVertical: true,
        description: stringResources.Snowflake_UserName_Desc
    }
};

export const roleControl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'role',
        label: stringResources.Role,
        isLabelVertical: true,
        description: stringResources.Snowflake_Role_Desc,
        required: false
    }
};

const oAuthAuthenticationInput: ControlMetadata[] = [
    nameControl,
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'oauthTokenEndpoint',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_OAuthTokenEndpoint,
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'clientId',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_ClientId,
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'clientSecret',
        label: stringResources.ModelName_ClientSecret,
        child: {
            type: 'parameterizable',
            attribute: 'clientSecret',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_ClientSecret,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Snowflake_DisplayText),
            stringResources.ModelName_ClientSecret)
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'scope',
            required: true,
            label: stringResources.Scope,
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'oauthUserName',
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            label: stringResources.ModelName_OAuthUserName,
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'oauthPassword',
        label: stringResources.ModelName_OAuthPassword,
        child: {
            type: 'parameterizable',
            attribute: 'oauthPassword',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_OAuthPassword,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ]
            }
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Snowflake_DisplayText),
            stringResources.ModelName_OAuthPassword)
    }
];

const additionalFieldsControl: ControlMetadata = {
    type: 'key-val-list',
    bind: 'additionalFields',
    label: stringResources.AdditionalConnectionStringFields
};

const akvControl: ControlMetadata = {
    type: 'azure-key-vault',
    enableAKVParameterization: true,
    bind: 'referedAKV',
    isLabelVertical: true,
    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Snowflake_DisplayText),
        'jdbc:snowflake://<accountname>.snowflakecomputing.com/?user=<username>&password=<password>&db=<database>&warehouse=<warehouse>&role=<role>')
};

const akvAuthenticationOptionsInput: ControlMetadata[] =
    [
        akvControl,
        {
            type: 'select-switch',
            bind: 'authenticationType',
            label: stringResources.ModelName_AuthenticationType,
            description: stringResources.Snowflake_AuthType_Desc,
            required: true,
            hideDropdownWhenOptionsLessThan2: true,
            options: [
                {
                    label: stringResources.Basic_DisplayText,
                    value: snowflakeAuthTypes.Basic
                },
                {
                    label: stringResources.AuthenticationType_OAuth,
                    value: snowflakeAuthTypes.OAuth,
                    hide: '{ !isExistingResourceUsingOAuth }'
                }
            ],
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: snowflakeAuthTypes.Basic,
            cases: [
                {
                    caseValue: snowflakeAuthTypes.OAuth,
                    children: [
                        ...oAuthAuthenticationInput
                    ]
                }
            ]
        }
    ];

const defaultAuthenticationOptionsInput: ControlMetadata[] =
    [
        {
            type: 'select-switch',
            bind: 'authenticationType',
            label: stringResources.ModelName_AuthenticationType,
            description: stringResources.Snowflake_AuthType_Desc,
            hideDropdownWhenOptionsLessThan2: true,
            required: true,
            options: [
                {
                    label: stringResources.Basic_DisplayText,
                    value: snowflakeAuthTypes.Basic
                },
                {
                    label: stringResources.AuthenticationType_OAuth,
                    value: snowflakeAuthTypes.OAuth,
                    hide: '{ !isExistingResourceUsingOAuth }'
                }
            ],
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: snowflakeAuthTypes.Basic,
            cases: [
                {
                    caseValue: snowflakeAuthTypes.Basic,
                    children: [
                        nameControl,
                        dynamicPasswordControl
                    ]
                },
                {
                    caseValue: snowflakeAuthTypes.OAuth,
                    children: oAuthAuthenticationInput
                }
            ]
        }
    ];

export const snowflakeMetadata: ILinkedServiceTypeMetadata = {
    type: adfModelTypeMap.snowflake.linkedServiceType,
    displayName: getConnectorV1Name(stringResources.Snowflake_DisplayText),
    isDeprecated: !GlobalVariables.showLegacy,
    notShowInTypeSelection: !GlobalVariables.showLegacy,
    warningMessage: format(
        stringResources.Connector_Legacy_IsNotSupportedWarning_alt2,
        stringResources.LinkedServiceLowerCase,
        getConnectorV2Name(stringResources.Snowflake_DisplayText)
    ),
    warningMessageHyperlink: 'https://learn.microsoft.com/azure/data-factory/connector-snowflake?tabs=data-factory', // Need to update
    supportedInTrident: true,
    disabled: __IS_FABRIC_DPE__,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: ResourceKind.Snowflake
    },
    supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-SnowFlake'
    },
    supportScriptActivity: true,
    supportDataFlowSource: true,
    supportDataFlowSink: true,
    dataflowSource: [dataflowSnowFlake],
    dataflowSink: [dataflowSnowFlake],
    isFirstClass: true,
    supportFirstClassParameterExperience: true,
    category: ['Database'],
    supportDatasetV2: true,
    dataflowSinkStagingSchema: true,
    supportPreCopyScript: true,
    copySinkTypes: [adfModelTypeMap.snowflake.copySinkType],
    copySourceTypes: [adfModelTypeMap.snowflake.copySourceType],
    controls: [
        {
            type: 'select-button-switch',
            required: true,
            isLabelVertical: true,
            options: [
                {
                    label: stringResources.ModelName_ConnectionString,
                    value: false
                },
                {
                    label: stringResources.AzureKeyVault_DisplayText,
                    value: true
                }
            ],
            bind: 'enableAKV',
            isNotRelatedToCredential: true,
            defaultValue: false,
            cases: [
                {
                    caseValue: true,
                    children: [
                        ...akvAuthenticationOptionsInput
                    ]
                },
                {
                    caseValue: false,
                    children: [
                        ...snowflakeCommonControls(),
                        ...defaultAuthenticationOptionsInput,
                        roleControl,
                        additionalFieldsControl
                    ]
                }
            ]
        }
    ]
};
